import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { routes } from 'routes/routes';
import { Button, Col, Link, Row, Text, Title } from 'ui';

type P = {
  isRequestLoading: boolean;
  onReplaceCheckMagicLink: () => Promise<void>;
  onUserClickBack: () => Promise<void>;
  requestAnotherMagicLink: () => void;
};

const formatTime = (time: number) => {
  const minutes = Math.floor(time / 60);
  const seconds = time - minutes * 60;
  const formattedMinutes = minutes > 0 ? `${minutes}m` : '';
  const formattedSeconds = seconds > 0 ? `${seconds}s` : '';
  return `${formattedMinutes} ${formattedSeconds}`;
};

const LinkWasSent = ({ isRequestLoading, onReplaceCheckMagicLink, onUserClickBack, requestAnotherMagicLink }: P) => {
  const { t } = useTranslation();

  const [timeToExpired, setTimeToExpired] = useState(300);
  const [isLinkResent, setIsLinkResent] = useState(false);

  useEffect(() => {
    if (timeToExpired === 0) {
      void onReplaceCheckMagicLink();
      return;
    }

    const intervalId = setInterval(() => {
      setTimeToExpired((currentTime) => currentTime - 1);
    }, 1000);
    return () => {
      clearInterval(intervalId);
    };
  }, [onReplaceCheckMagicLink, timeToExpired]);

  const resendMail = () => {
    setTimeToExpired(300);
    setIsLinkResent(true);
    requestAnotherMagicLink();
  };

  return (
    <Row>
      <Col span={24}>
        <Title level={2} marginBottom>
          {isLinkResent ? t('Log in link resent') : t('Log in link sent')}
        </Title>
      </Col>
      <Col className="onboarding-link-was-sent__was-sent-text" span={24}>
        <Row gutter={[0, 'ms']}>
          <Col span={24}>
            <Text>
              {t(
                'We have sent you an email with a log in link. Go to your inbox, check your email, and click the button with the log in link. It will redirect you back to this app and automatically log you in.',
              )}
            </Text>
          </Col>
          <Col span={24}>
            <Text>
              <Trans i18nKey="loginHelpText">
                If you didn&apos;t receive the log in link, please try again or <Link to={routes.signUp}>sign up.</Link>
              </Trans>
            </Text>
          </Col>

          <Col span={24}>
            <Row align="middle" justify="center">
              <Col span={12}>
                <Text>
                  {t('Link expires in')}:{' '}
                  <Text strong type="secondary">
                    {formatTime(timeToExpired)}
                  </Text>
                </Text>
              </Col>
              <Col className="onboarding-password-login-form__forgot-link" span={12}>
                <Button
                  disabled={isRequestLoading}
                  loading={isRequestLoading}
                  onClick={resendMail}
                  size="large"
                  type="link-primary"
                >
                  {t('Request new link')}
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>
      </Col>

      <Col className="onboarding-link-was-sent__buttons-section" span={24}>
        <Button
          className="onboarding-link-was-sent__link-sent-buttons"
          onClick={async () => {
            await onUserClickBack();
          }}
          size="large"
        >
          {t(`Back to Log in`)}
        </Button>
      </Col>
    </Row>
  );
};

export default LinkWasSent;
